import { Address } from './index';
import { AxiosResponse } from 'axios';
import { FilterModel } from '@hooks/useFiltersSearchParams';
import { getParams, sellerApiClient } from '@api/apiClient/sellerApiClient';
import { PaginationModel } from '@hooks/usePagingSearchParams';
import { TPhotosData } from '../../pages/ReturnsPage/types';
import { useQuery } from 'react-query';

const defaultParams: PaginationModel = { page: 1, pageSize: 1 };

export const ONLY_AVAILABLE_FOR_RESALE_FILTER = 'onlyAvailableForResale';

export type TRequestParams = {
  filters?: FilterModel[];
  page: PaginationModel;
};

export type TAmountWithCurrency = {
  amount: number;
  currency: string;
};

export type TLinkedParcel = {
  id: string;
  returnPostingNumber: string;
  trackingNumber: string;
}

export type TAttachmentItem = {
  name: string;
  nameEn: string;
  url: string;
  price: TAmountWithCurrency;
  quantity: number;
};

export type TResaleOrderExcessItem = {
  name: string;
  url: string;
  price: TAmountWithCurrency;
  quantity: number;
};

export type TAvailableForResaleOrder = {
  id: string;
  trackNumber: string;
  excessItems: TResaleOrderExcessItem[];
};

export type TAwaitingParcel = {
  deadline: Date;
  defectReason: string;
  defectReasonType: string;
  directStreamExternalId: string;
  directStreamProvider: string;
  id: string;
  isAvailableForLastMileResale: boolean;
  isReexportBlocked: boolean;
  isResaleBlocked: boolean;
  lastMileResalePrice: TAmountWithCurrency;
  linkedParcelCustomerReturns: TLinkedParcel[];
  priceCny: number;
  priceRub: number;
  productCondition: string;
  productConditionType: string;
  reexportCancelReason: string;
  reexportPrice: TAmountWithCurrency;
  resaleCancelReason: string;
  resaleBlockReason: 'RepeatedReturn' | 'LastMileOrderNotReceivedInTime' | null;
  resalePrice: TAmountWithCurrency;
  deliverToLocalPrice: TAmountWithCurrency | null;
  utilizationCompensation: TAmountWithCurrency | null;
  returnPostingNumber: string;
  seller: {
    id: string;
    name: string;
  };
  title: string;
  trackingNumber: string;
  url: string;
  marketplaceType: string;
  attachments: TAttachmentItem[];
  availableForResaleOrders: TAvailableForResaleOrder[];
  isIncorrectLocalAddress: boolean;
};

export type TAwaitingParcelsResponse = {
  total: number;
  items: TAwaitingParcel[];
};

export type TDisposalParcel = {
  defectReason: string;
  defectReasonType: string;
  directStreamExternalId: string;
  id: string;
  linkedParcelId?: string;
  priceCny: number;
  priceRub: number;
  productCondition: string;
  productConditionType: string;
  returnOrderNumber: string;
  returnPostingNumber: string;
  seller: {
    id: string;
    name: string;
  };
  title: string;
  trackingNumber: string;
  url: string;
  utilizationVoteDate: Date;
  vendorCode: string;
  marketplaceType: string;
  utilizationVoteInitiator: string;
  compensationStatus: 'NotApplicable' | 'Pending' | 'Received';
  canCancelVote: boolean;
};

export type TDisposalParcelsResponse = {
  total: number;
  items: TDisposalParcel[];
};

export type TProcessingParcel = {
  auditObtainedDate: Date;
  changingReturnTypeBy: string;
  comments: {
    createdAt: Date;
    id: string;
    text: string;
  }[];
  createdAt: Date;
  defectReason: string;
  defectReasonType: string;
  dimensions: {
    height: {
      unit: string;
      value: number;
    };
    length: {
      unit: string;
      value: number;
    };
    width: {
      unit: string;
      value: number;
    };
  };
  directStreamExternalId: string;
  directStreamProvider: string;
  events: {
    createdAt: Date;
    newStatus: number;
  }[];
  hasBattery: boolean;
  id: string;
  linkedParcelId?: string;
  paidDate: Date;
  priceCny: number;
  priceRub: number;
  productCondition: string;
  productConditionType: string;
  quantity: {
    value: number;
  };
  reexportPrice: TAmountWithCurrency;
  returnOrderNumber: string;
  returnPhotos: TPhotosData;
  returnPostingNumber: string;
  returnReason: string;
  returnType: number;
  seller: {
    address: string;
    email: string;
    id: string;
    marketplaceName: string;
    name: string;
  };
  status: string;
  title: string;
  trackingNumber: string;
  url: string;
  vendorCode: string;
  weight: {
    unit: string;
    value: number;
  };
  marketplaceType: string;
};

export type TProcessingParcelsResponse = {
  total: number;
  items: TProcessingParcel[];
};

export type TResaleParcel = {
  defectReason: string;
  defectReasonType: string;
  directStreamExternalId: string;
  directStreamTrackingNumber: string;
  id: string;
  marketplaceType: string;
  newOrderId: string;
  newOrderNumber: string;
  productCondition: string;
  productConditionType: string;
  resaleVotedDate: Date;
  resalePrice: TAmountWithCurrency;
  returnPostingNumber: string;
  seller: {
    id: string;
    name: string;
  };
  title: string;
  trackingNumber: string;
  url: string;
};

export type TResaleParcelsResponse = {
  total: number;
  items: TResaleParcel[];
};

export type TLastMileResaleParcel = {
  id: string;
  returnPostingNumber: string;
  trackingNumber: string | null;
  title: string;
  vendorCode: string;
  url: string;
  marketplaceType: string;
  stockedAt: Date | null;
  decisionDeadline: Date | null;
  seller: {
    id: string;
    name: string;
  };
};

export type TLastMileResaleParcelsResponse = {
  total: number;
  items: TLastMileResaleParcel[];
};

export type TReturnsParcel = {
  customerReturnStatus?: string;
  defectReason: string;
  defectReasonType: string;
  directStreamExternalId: string;
  id: string;
  linkedParcelId?: string;
  priceCny: number;
  priceRub: number;
  productCondition: string;
  productConditionType: string;
  reexportVoteDate: Date;
  returnOrderNumber: string;
  returnPostingNumber: string;
  seller: {
    id: string;
    name: string;
  };
  status: string;
  title: string;
  trackingNumber: string;
  url: string;
  vendorCode: string;
  marketplaceType: string;
};

export type TReturnsParcelsResponse = {
  total: number;
  items: TReturnsParcel[];
};

export type TCountryTariff = {
  country: string;
  directStreamProviderType: string;
  itemCost: TAmountWithCurrency;
  kilogramCost: TAmountWithCurrency;
};

export type TReturnCost = {
  countryTariffs: TCountryTariff[];
  disposalCost?: TAmountWithCurrency;
};

export type TReturnCostResponse = TReturnCost;

export type TStatus = {
  id: string;
  name: string;
};

export type TStatusesResponse = TStatus[];

export type TState = {
  id: string;
  name: string;
};

export type TStatesResponse = TState[];

export type TReturnStreamTrackingEvent = {
  id: string;
  eventType: number;
  eventName: string;
  occurredAt: Date;
}

export type TParcel = {
  item: {
    auditObtainedDate: Date;
    changingReturnTypeBy: string;
    checkingPhotos: TPhotosData;
    comments: {
      createdAt: Date;
      id: string;
      text: string;
    }[];
    createdAt: Date;
    defectReason: string;
    defectReasonType: string;
    dimensions: {
      height: {
        unit: string;
        value: number;
      };
      length: {
        unit: string;
        value: number;
      };
      width: {
        unit: string;
        value: number;
      };
    };
    directStreamExternalId: string;
    directStreamProvider: string;
    events: {
      createdAt: Date;
      newStatus: number;
    }[];
    hasBattery: boolean;
    id: string;
    isReexportBlocked: boolean;
    paidDate: Date;
    priceCny: number;
    priceRub: number;
    productCondition: string;
    productConditionType: string;
    productExtendedCondition: string;
    quantity: {
      value: number;
    };
    reexportPrice: TAmountWithCurrency;
    reexportCancelReason: string;
    resaleCancelReason: string;
    resalePrice: TAmountWithCurrency;
    deliverToLocalPrice: TAmountWithCurrency | null;
    returnOrderNumber: string;
    returnPhotos: TPhotosData;
    returnPostingNumber: string;
    returnReason: string;
    returnStreamTrackingEvents: TReturnStreamTrackingEvent[];
    returnType: number;
    seller: {
      address: string;
      email: string;
      id: string;
      marketplaceName: string;
      name: string;
    };
    status: string;
    title: string;
    trackingNumber: string;
    trackingInfo?: {
      trackNumber: string;
      url: string;
      deliveryService: string;
    };
    url: string;
    vendorCode: string;
    weight: {
      unit: string;
      value: number;
    };
    marketplaceType: string;
    linkedParcelId?: string;
    countryCode: string;
  }
};

export type TParcelResponse = TParcel;

const addedFilters: string[] = ['New', 'NoPossibilityToCheck', 'Unknown'];

export const getAwaiting = async (params: TRequestParams): Promise<TAwaitingParcelsResponse> => {
  const { filters, page } = params;
  const filtersSafe: FilterModel[] = filters ?? [];
  const desiredFilter: FilterModel | undefined = filters?.find((item: FilterModel) => item.name === 'productConditionType');
  const condition: boolean = !!desiredFilter && desiredFilter?.value.includes('Normal');
  const updatedFilters: FilterModel[] =
      condition
          ? filtersSafe.map((item) => {
            if (item.name === 'productConditionType') {
              return {
                ...item,
                value: [...item.value, ...addedFilters]
              };
            }
            return item;
          })
          : filtersSafe;
  const response = await sellerApiClient.get<TAwaitingParcelsResponse>('/v1/customer-returns/waitingfordecision/page', {
    params: getParams(page, updatedFilters),
    paramsSerializer: { indexes: null }
  });
  return response.data;
};

export const getDisposal = async (params: TRequestParams): Promise<TDisposalParcelsResponse> => {
  const { filters, page } = params;
  const response = await sellerApiClient.get<TDisposalParcelsResponse>('/v1/customer-returns/utilization/page', {
    params: getParams(page, filters),
    paramsSerializer: { indexes: null }
  });
  return response.data;
};

export const getProcessing = async (params: TRequestParams): Promise<TProcessingParcelsResponse> => {
  const { filters, page } = params;
  const response = await sellerApiClient.get<TProcessingParcelsResponse>('/v1/customer-returns/page', {
        params: getParams(page, filters),
        paramsSerializer: { indexes: null }
      }
  );
  return response.data;
};

export const getReturns = async (params: TRequestParams): Promise<TReturnsParcelsResponse> => {
  const { filters, page } = params;
  const response = await sellerApiClient.get<TReturnsParcelsResponse>('/v1/customer-returns/reexport/page', {
    params: getParams(page, filters),
    paramsSerializer: { indexes: null }
  });
  return response.data;
};

export const getResale = async (params: TRequestParams): Promise<TResaleParcelsResponse> => {
  const { filters, page } = params;
  const response = await sellerApiClient.get<TResaleParcelsResponse>('/v1/customer-returns/resale/page', {
    params: getParams(page, filters),
    paramsSerializer: { indexes: null }
  });
  return response.data;
};

export const getLastMileResale = async (params: TRequestParams): Promise<TLastMileResaleParcelsResponse> => {
  const { filters, page } = params;
  const response = await sellerApiClient.get<TLastMileResaleParcelsResponse>('/v1/customer-returns/resale/last-mile/page', {
    params: getParams(page, filters),
    paramsSerializer: { indexes: null }
  });
  return response.data;
};

export const getReturnCost = async (): Promise<TReturnCostResponse> => {
  const response = await sellerApiClient.get<TReturnCostResponse>('/v1/customer-returns/countryTariffs');
  return response.data;
};

export const getStatuses = async (): Promise<TStatusesResponse> => {
  // const response = await sellerApiClient.get<TStatusesResponse>('/v1/statuses');
  // return response.data;
  return ([
    { id: 'InProcess', name: 'InProcess' },
    { id: 'MovingToWarehouse', name: 'MovingToWarehouse' }
  ]);
};

export const getStates = async (): Promise<TStatesResponse> => {
  // const response = await sellerApiClient.get<TStatesResponse>('/v1/states');
  // return response.data;
  return ([
    { id: 'Defect', name: 'Defect' },
    { id: 'Normal', name: 'Normal' }
  ]);
};

export const getParcel = async (parcelId: string): Promise<TParcelResponse> => {
  const response = await sellerApiClient.get<TParcelResponse>(`/v1/customer-returns/customer-return?customerReturnId=${parcelId}`);
  return response.data;
};

export const getParcelPhoto = async (storageKey: string): Promise<AxiosResponse> => {
  return await sellerApiClient.get(`/v1/customer-returns/files/${storageKey}`, {
    responseType: 'blob'
  });
};

export type TReturnOrderAddress = {
  address: Address | undefined;
  addressEnglish?: Address | undefined;
  contactInfo: {
    name: string | undefined;
    phone: string | undefined;
  };
};

export type TReturnOrderParams = TReturnOrderAddress & {
  customerReturnIds: string[];
};

export type TReturnOrderResponse = {
  reexportOrderId: string;
};

export const sendReturnOrder = async (params: TReturnOrderParams): Promise<TReturnOrderResponse> => {
  const response = await sellerApiClient.post<TReturnOrderResponse>('/v1/customer-returns/re-export/order', params);
  return response.data;
};

export type TCheckOrderStatusResponse = {
  id: string;
  reason?: string;
  status: string;
};

export const checkReexportOrderStatus = async (reexportOrderId: string): Promise<TCheckOrderStatusResponse> => {
  const response = await sellerApiClient.get(`/v1/customer-returns/re-export/order/${reexportOrderId}/status`);
  return response.data;
};

export type TDisposalOrderParams = {
  customerReturnIds: string[];
};

export const sendDisposalOrder = async (params: TDisposalOrderParams): Promise<void> => {
  const response = await sellerApiClient.post<void>('/v1/customer-returns/utilization/vote', params);
  return response.data;
};

export const cancelUtilizationVote = async (customerReturnIds: string[]): Promise<void> => {
  const response = await sellerApiClient.post<void>('/v1/customer-returns/utilization/vote/cancel', { customerReturnIds });
  return response.data;
};

export type TResaleSelect = {
  customerReturnId: string;
  orderParcelId: string;
};

export type TResaleOrderParams = {
  resaleSelects: TResaleSelect[];
};

export type TResaleOrderResponse = {
  orderId: string;
};

export const sendResaleOrder = async (params: TResaleOrderParams): Promise<TResaleOrderResponse> => {
  const response = await sellerApiClient.post<TResaleOrderResponse>('/v1/customer-returns/resale/order', params);
  return response.data;
};

export const checkResaleOrderStatus = async (id: string): Promise<TCheckOrderStatusResponse> => {
  const response = await sellerApiClient.get(`/v1/customer-returns/resale/order/${id}/status`);
  return response.data;
};

export type TLastMileResaleOrderParams = {
  customerReturnIds: string[];
};

export const sendLastMileResaleOrder = async (params: TLastMileResaleOrderParams): Promise<TResaleOrderResponse> => {
  const response = await sellerApiClient.post<TResaleOrderResponse>('/v1/customer-returns/resale/last-mile/order', params);
  return response.data;
};

export const checkLastMileResaleOrderStatus = async (id: string): Promise<TCheckOrderStatusResponse> => {
  const response = await sellerApiClient.get(`/v1/customer-returns/resale/last-mile/order/${id}/status`);
  return response.data;
};

export type TLastMileWarehouse = {
  id: string;
  name: string;
  type: string;
  externalId: string;
  businessId: number | null;
  warningReason?: string | null;
};

export type TLastMileWarehousesResponse = {
  warehouses: TLastMileWarehouse[];
};

export const getLastMileWarehouses = async (): Promise<TLastMileWarehousesResponse> => {
  const response = await sellerApiClient.get<TLastMileWarehousesResponse>('/v1/customer-returns/resale/last-mile/warehouse');
  return response.data;
};

export type TLastMileSeller = {
  id: string;
  name: string;
};

export type TLastMileSellersResponse = {
  items: TLastMileSeller[];
};

export const getLastMileSellers = async (marketplaceType: string = 'Yandex'): Promise<TLastMileSellersResponse> => {
  const response = await sellerApiClient.get<TLastMileSellersResponse>('/v1/customer-returns/resale/last-mile/sellers', {
    params: { marketplaceType }
  });
  return response.data;
};

export type TCreateLastMileYandexWarehouseParams = {
  sellerId: string;
};

export type TCreateLastMileWarehouseResponse = {
  warehouseId: string;
};

export const createLastMileYandexWarehouse = async (params: TCreateLastMileYandexWarehouseParams): Promise<TCreateLastMileWarehouseResponse> => {
  const response = await sellerApiClient.post<TCreateLastMileWarehouseResponse>('/v1/customer-returns/resale/last-mile/warehouse/yandex', params);
  return response.data;
};

export const deleteLastMileWarehouse = async (id: string): Promise<void> => {
  await sellerApiClient.delete<void>(`/v1/customer-returns/resale/last-mile/warehouse/${id}`);
};

export type TDeliverToLocalOrderParams = TReturnOrderAddress & {
  customerReturnIds: string[];
  comment?: string;
};

export type TDeliverToLocalOrderResponse = {
  orderId: string;
};

export const sendDeliverToLocalOrder = async (params: TDeliverToLocalOrderParams): Promise<TDeliverToLocalOrderResponse> => {
  const response = await sellerApiClient.post<TDeliverToLocalOrderResponse>('/v1/customer-returns/deliver-to-local/order', params);
  return response.data;
};

export const checkDeliverToLocalOrderStatus = async (orderId: string): Promise<TCheckOrderStatusResponse> => {
  const response = await sellerApiClient.get(`/v1/customer-returns/deliver-to-local/order/${orderId}/status`);
  return response.data;
};

export type TResaleCancelParams = {
  customerReturnId: string;
};

export const sendResaleCancel = async (params: TResaleCancelParams): Promise<void> => {
  const response = await sellerApiClient.post<void>('/v1/customer-returns/resale/cancel/', params);
  return response.data;
};

export const useAwaitingCounter = (enabled: boolean = true) => {
  const { data, refetch } = useQuery({
    queryKey: ['awaiting', defaultParams],
    queryFn: () => getAwaiting({
      filters: [],
      page: defaultParams
    }),
    retry: 1,
    select: ({ total }) => total ?? 0,
    enabled: enabled
  });
  return { data, refetch };
};

export const useAvailableForResaleCounter = (enabled: boolean = true) => {
  const { data, isError, isFetching, refetch } = useQuery({
    queryKey: ['awaiting', 'availableForResale', defaultParams],
    queryFn: () => getAwaiting({
      filters: [{ name: ONLY_AVAILABLE_FOR_RESALE_FILTER, value: ['true'] }],
      page: defaultParams
    }),
    retry: 1,
    select: ({ total }) => total ?? 0,
    enabled
  });
  return { data, isError, isFetching, refetch };
};

export const useDisposalCounter = (enabled: boolean = true) => {
  const { data, refetch } = useQuery({
    queryKey: ['disposal', defaultParams],
    queryFn: () => getDisposal({
      filters: [],
      page: defaultParams
    }),
    retry: 1,
    select: ({ total }) => total ?? 0,
    enabled: enabled
  });
  return { data, refetch };
};

export const useProcessingCounter = (enabled: boolean = true) => {
  const { data, refetch } = useQuery({
    queryKey: ['processing', defaultParams],
    queryFn: () => getProcessing({
      filters: [],
      page: defaultParams
    }),
    retry: 1,
    select: ({ total }) => total ?? 0,
    enabled: enabled
  });
  return { data, refetch };
};

export const useReturnsCounter = (enabled: boolean = true) => {
  const { data, refetch } = useQuery({
    queryKey: ['returns', defaultParams],
    queryFn: () => getReturns({
      filters: [],
      page: defaultParams
    }),
    retry: 1,
    select: ({ total }) => total ?? 0,
    enabled: enabled
  });
  return { data, refetch };
};

export const useResaleCounter = (enabled: boolean = true) => {
  const { data, refetch } = useQuery({
    queryKey: ['resale', defaultParams],
    queryFn: () => getResale({
      filters: [],
      page: defaultParams
    }),
    retry: 1,
    select: ({ total }) => total ?? 0,
    enabled: enabled
  });
  return { data, refetch };
};

export const useLastMileResaleCounter = (enabled: boolean = true) => {
  const { data, refetch } = useQuery({
    queryKey: ['lastMileResale', defaultParams],
    queryFn: () => getLastMileResale({
      filters: [],
      page: defaultParams
    }),
    retry: 1,
    select: ({ total }) => total ?? 0,
    enabled: enabled
  });
  return { data, refetch };
};

export type TAutoResaleReturnsParams = {
  enabled: boolean;
  resalePrice?: TAmountWithCurrency;
};

export const getAutoResaleReturnsData = async (): Promise<TAutoResaleReturnsParams> => {
  const response = await sellerApiClient.get<TAutoResaleReturnsParams>('/v1/customer-returns/resale/auto/settings');
  return response.data;
};

export const sendAutoResaleReturnsData = async (params: TAutoResaleReturnsParams): Promise<void> => {
  const response = await sellerApiClient.post<void>('/v1/customer-returns/resale/auto/settings', params);
  return response.data;
};

export type TAutoResaleReturnsMissingAmountResponse = {
  amount: TAmountWithCurrency;
};

export const getAutoResaleReturnsMissingAmount = async (): Promise<TAutoResaleReturnsMissingAmountResponse> => {
  const response = await sellerApiClient.get<TAutoResaleReturnsMissingAmountResponse>('/v1/auto-actions/missing-amount/total');
  return response.data;
};

export type TApproachingDeadlineResponse = {
  count: number;
};

export const getApproachingDeadline = async (): Promise<TApproachingDeadlineResponse> => {
  const response = await sellerApiClient.get<TApproachingDeadlineResponse>('/v1/customer-returns/approaching-deadline');
  return response.data;
};

export type TValidateAddressResponse = {
  isValid: true;
  message: string;
  addressComponents: {
    province: string;
    city: string;
    district: string;
    street: string;
    streetNumber: string;
  }
}

export const validateAddress = async ({ rawAddress }: { rawAddress: string }): Promise<TValidateAddressResponse> => {
  const response = await sellerApiClient.post<TValidateAddressResponse>(
    '/v1/customer-returns/deliver-to-local/validate-address',
    { rawAddress },
  );
  return response.data;
};
